import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, CircularProgress, Container, Grid, Hidden, IconButton, Typography } from '@material-ui/core';
import { KeyboardBackspace as KeyboardBackspaceIcon, MoreVert as MoreVertIcon } from '@material-ui/icons';
import { Menu, Modal } from '@mvrb-frontend/shared-ui';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  menu: {
    color: theme.palette.common.white,
  },
  actions: {
    alignItems: 'baseline',
    justifyContent: 'flex-end',
  },
  submitButton: {
    whiteSpace: 'nowrap',
  },
  cancelButton: {
    borderColor: theme.palette.common.white,
    color: theme.palette.common.white,
    whiteSpace: 'nowrap',
  },
}));

const OrganizationHeader = (props: any) => {
  const classes = useStyles();
  const {
    isFetching,
    isForm,
    headerText,
    onSubmit,
    disableSubmitButton,
    submitButtonText,
    isBackButtonVisible,
    backModalTitleText = 'Back',
    backModalBodyText = 'If you go back now, all the information that you have filled in will be lost. Are you sure you want to exit this page?',
    onBackConfirm,
    isCancelButtonVisible = true,
    cancelButtonText = 'Cancel',
    cancelModalTitleText = 'Cancel',
    cancelModalBodyText = 'If you go back, all the information that you have filled in will be lost. Are you sure you want to exit this page?',
    onCancelConfirm,
    isDeleteButtonVisible = false,
    deleteButtonText = 'Delete',
    deleteModalTitleText = 'Delete',
    deleteModalBodyText = 'Delete item?',
    onDeleteConfirm,
  } = props;
  const [cancelOpen, setCancelOpen] = useState(false);
  const [backOpen, setBackOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const openCancelModal = () => {
    setCancelOpen(true);
  };

  const closeCancelModal = () => {
    setCancelOpen(false);
  };

  const openBackModal = () => {
    setBackOpen(true);
  };

  const closeBackModal = () => {
    setBackOpen(false);
  };

  const openDeleteModal = () => {
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteOpen(false);
  };

  const saveUserProfile = () => {
    onSubmit();
  };

  return (
    <>
      {/** Header */}
      <Box p={2} className={classes.root}>
        <Container maxWidth="lg">
          <Grid container>
            <Grid item xs={10} sm={8} md={isCancelButtonVisible && isDeleteButtonVisible ? 6 : 8}>
              <Box display="flex">
                {isBackButtonVisible && (
                  <Box>
                    <IconButton onClick={openBackModal} color="inherit">
                      <KeyboardBackspaceIcon />
                    </IconButton>
                  </Box>
                )}
                <Box ml={2}>
                  <Typography variant="h3" color="inherit">
                    {headerText}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={2} sm={4} md={isCancelButtonVisible && isDeleteButtonVisible ? 6 : 4}>
              {isForm && (
                <Hidden smUp>
                  <Menu
                    options={[
                      isCancelButtonVisible && { label: cancelButtonText, onClick: openCancelModal },
                      isDeleteButtonVisible && { label: deleteButtonText, onClick: openDeleteModal },
                      { label: submitButtonText, onClick: saveUserProfile },
                    ].filter(Boolean)}
                    label={<MoreVertIcon className={classes.menu} />}
                  />
                </Hidden>
              )}
              <Hidden xsDown>
                <Grid container spacing={1} className={classes.actions}>
                  {isCancelButtonVisible && (
                    <Grid item>
                      <Button
                        className={classes.cancelButton}
                        size="large"
                        variant="outlined"
                        onClick={openCancelModal}
                      >
                        {cancelButtonText}
                      </Button>
                    </Grid>
                  )}
                  {isDeleteButtonVisible && (
                    <Grid item>
                      <Button
                        className={classes.cancelButton}
                        size="large"
                        variant="outlined"
                        onClick={openDeleteModal}
                      >
                        {deleteButtonText}
                      </Button>
                    </Grid>
                  )}
                  {isForm && (
                    <React.Fragment>
                      <Grid item>
                        <Button
                          className={classes.submitButton}
                          size="large"
                          variant="contained"
                          color="secondary"
                          type={'button'}
                          onClick={saveUserProfile}
                          disabled={disableSubmitButton}
                        >
                          {isFetching ? <CircularProgress size={24} /> : submitButtonText}
                        </Button>
                      </Grid>
                    </React.Fragment>
                  )}
                </Grid>
              </Hidden>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/** Modal Dialogs */}
      <Modal
        isVisible={cancelOpen}
        titleText={cancelModalTitleText}
        modalText={cancelModalBodyText}
        cancelText={'Cancel'}
        confirmText={'Continue'}
        onCancel={closeCancelModal}
        onConfirm={() => {
          closeCancelModal();
          if (onCancelConfirm) onCancelConfirm();
        }}
      />
      <Modal
        isVisible={backOpen}
        titleText={backModalTitleText}
        modalText={backModalBodyText}
        cancelText={'Cancel'}
        confirmText={'Continue'}
        onCancel={closeBackModal}
        onConfirm={() => {
          closeBackModal();
          if (onBackConfirm) onBackConfirm();
        }}
      />
      <Modal
        isVisible={deleteOpen}
        titleText={deleteModalTitleText}
        modalText={deleteModalBodyText}
        cancelText={'Cancel'}
        confirmText={'Delete Now'}
        onCancel={closeDeleteModal}
        onConfirm={() => {
          closeDeleteModal();
          if (onDeleteConfirm) onDeleteConfirm();
        }}
      />
    </>
  );
};

OrganizationHeader.propTypes = {
  isFetching: PropTypes.bool,
  isForm: PropTypes.bool, // show action button or not
  headerText: PropTypes.string.isRequired, // form name
  onSubmit: PropTypes.any.isRequired,
  disableSubmitButton: PropTypes.bool,
  submitButtonText: PropTypes.string.isRequired,
  isBackButtonVisible: PropTypes.bool,
  backModalTitleText: PropTypes.string,
  backModalBodyText: PropTypes.string,
  onBackConfirm: PropTypes.any,
  isCancelButtonVisible: PropTypes.bool,
  cancelButtonText: PropTypes.string,
  cancelModalTitleText: PropTypes.string,
  cancelModalBodyText: PropTypes.string,
  onCancelConfirm: PropTypes.any,
  isDeleteButtonVisible: PropTypes.bool,
  deleteButtonText: PropTypes.string,
  deleteModalTitleText: PropTypes.string,
  deleteModalBodyText: PropTypes.string,
  onDeleteConfirm: PropTypes.any,
};

export default OrganizationHeader;
